/**
 * Terrain height.
 *
 * The map was a flat plane 180m across — nowhere to go, nothing to find, and every
 * direction identical. Height does more for that than any amount of texture work: a
 * ridge you can see over, a hollow you can hide in, a slope that slows you down.
 *
 * The heightfield is a function rather than stored data, so anything can ask "how high
 * is the ground here" — the player, every animal, the grass, the trees — without a
 * lookup table, and it stays identical between clients for multiplayer.
 */
import * as THREE from 'three'
import { MAP_HALF_EXTENT, WATER } from './map'

/**
 * How deep the pond's bed is carved below the land around it. Without this the terrain
 * rose through the water surface — the ground beside the pond sat at -4.17 while the
 * water sat at -4.31, so the land poked through and the pond read as broken.
 */
const POND_DEPTH = 2.2
/** The land level the pond's bed is measured from — it sits in The Basin. */
const POND_BASE = -6.5
/** How deep the water stands over the bed. */
const POND_FILL = 1.5
/** How far the bank finally rises above the waterline. */
const BANK_LIP = 0.6

/** Peak height of the terrain in metres. */
const AMPLITUDE = 11
/** Broad hills; smaller is wider. */
const HILL_SCALE = 0.0135
/** Finer undulation on top of the hills. */
const DETAIL_SCALE = 0.055

/** Landmarks worth walking toward. Each is a place, not scenery. */
export interface Landmark {
  id: string
  name: string
  x: number
  z: number
  radius: number
  /** How much it lifts (or sinks) the ground under it. */
  lift: number
}

export const LANDMARKS: Landmark[] = [
  // A high point you can see the map from — the reward for climbing.
  { id: 'ridge', name: 'The Ridge', x: -52, z: -44, radius: 34, lift: 20 },
  // A bowl that hides you from everything outside it.
  { id: 'hollow', name: 'The Hollow', x: 46, z: -38, radius: 26, lift: -9 },
  // A second peak, so the skyline has shape from anywhere.
  { id: 'knuckle', name: 'Knuckle Rock', x: 58, z: 52, radius: 22, lift: 15 },
  // The valley the water sits in.
  { id: 'basin', name: 'The Basin', x: -40, z: 46, radius: 30, lift: -7 },
]

function hash(x: number, z: number): number {
  // The offsets matter. sin is odd, so without them hash(-x, -z) mirrors hash(x, z)
  // and the whole terrain is subtly symmetric about the world axes --- which drew a
  // faint crease straight through the spawn clearing along x = 0 and z = 0. Measured:
  // the height field's curvature jumped sevenfold across x = 0. Shifting the origin of
  // the hash puts that symmetry line somewhere nobody stands.
  const n = Math.sin(x * 127.1 + z * 311.7 + 74.7) * 43758.5453
  return n - Math.floor(n)
}

/** Smooth value noise — cheap, and identical everywhere it is evaluated. */
function noise(x: number, z: number): number {
  const xi = Math.floor(x)
  const zi = Math.floor(z)
  const xf = x - xi
  const zf = z - zi
  const u = xf * xf * (3 - 2 * xf)
  const v = zf * zf * (3 - 2 * zf)
  const a = hash(xi, zi)
  const b = hash(xi + 1, zi)
  const c = hash(xi, zi + 1)
  const d = hash(xi + 1, zi + 1)
  return a * (1 - u) * (1 - v) + b * u * (1 - v) + c * (1 - u) * v + d * u * v
}

/**
 * Ground height at a world position. The single source of truth — the mesh, the player,
 * the animals, the trees and the grass all read this, so nothing can end up floating or
 * buried.
 */
export function heightAt(x: number, z: number): number {
  let h = 0
  h += (noise(x * HILL_SCALE, z * HILL_SCALE) - 0.5) * AMPLITUDE
  h += (noise(x * DETAIL_SCALE, z * DETAIL_SCALE) - 0.5) * AMPLITUDE * 0.22

  for (const landmark of LANDMARKS) {
    const d = Math.hypot(x - landmark.x, z - landmark.z)
    if (d < landmark.radius) {
      // Smooth falloff so a landmark blends into the land instead of sitting on it.
      const t = 1 - d / landmark.radius
      h += landmark.lift * t * t * (3 - 2 * t)
    }
  }

  // Flatten the spawn area, so the game does not open on a slope.
  const spawn = Math.hypot(x, z)
  if (spawn < 16) h *= Math.max(0, (spawn - 6) / 10)

  // Carve the pond's basin. Inside the water's own radius the bed is pushed to a flat
  // floor rather than merely dipped — a smooth dip left the bank rising through the
  // water disc at its edge, so the pond appeared to cut into the hillside. Outside that,
  // the rim blends back up to the surrounding land instead of forming a cliff.
  const toWater = Math.hypot(x - WATER.x, z - WATER.z)
  const rim = WATER.radius * 1.9
  if (toWater < rim) {
    const bedLevel = POND_BASE - POND_DEPTH
    if (toWater <= WATER.radius) {
      h = bedLevel
    } else {
      const t2 = 1 - (toWater - WATER.radius) / (rim - WATER.radius)
      const eased = t2 * t2 * (3 - 2 * t2)
      h = h * (1 - eased) + bedLevel * eased
      /*
       * Give the pond a bank.
       *
       * Kate, 2026-08-24: "there's a big white circle in the middle." Part of that was
       * the mirror-bright material, and part was this: the blend back up to open ground
       * was so gradual that the land a metre outside the pond still sat 0.9m *below*
       * the water surface. The water sheet stopped at its own radius, so its edge hung
       * in the air over lower ground --- a disc floating on the grass rather than water
       * held in a hollow. The ground now climbs past the waterline within a metre and a
       * half of the edge, which is what contains it.
       */
      const surface = bedLevel + POND_FILL
      const climb = Math.min(1, (toWater - WATER.radius) / 1.5)
      h = Math.max(h, surface + BANK_LIP * climb)
    }
  }

  return h
}

/**
 * The height of the pond's surface: just under the lowest point of its rim, so the water
 * plane is never intersected by its own banks.
 */
export function waterSurfaceHeight(): number {
  // The bed is flat inside the water's radius, so the surface simply sits above it —
  // deep enough to read as water, below the rim that surrounds it.
  return POND_BASE - POND_DEPTH + POND_FILL
}

/** Surface normal, for orienting things to the slope. */
export function normalAt(x: number, z: number): THREE.Vector3 {
  const e = 0.6
  const hx = heightAt(x + e, z) - heightAt(x - e, z)
  const hz = heightAt(x, z + e) - heightAt(x, z - e)
  return new THREE.Vector3(-hx, 2 * e, -hz).normalize()
}

/** How steep the ground is here, 0 flat to 1 sheer. Steep ground slows you down. */
export function slopeAt(x: number, z: number): number {
  const n = normalAt(x, z)
  return 1 - Math.max(0, n.y)
}

/** Build the visible terrain mesh from the same function everything else reads. */
export function createTerrain(material: THREE.Material, resolution = 200): THREE.Mesh {
  const span = MAP_HALF_EXTENT * 2
  const geometry = new THREE.PlaneGeometry(span, span, resolution, resolution)
  geometry.rotateX(-Math.PI / 2)

  const position = geometry.getAttribute('position') as THREE.BufferAttribute
  for (let i = 0; i < position.count; i++) {
    const x = position.getX(i)
    const z = position.getZ(i)
    position.setY(i, heightAt(x, z))
  }
  position.needsUpdate = true
  geometry.computeVertexNormals()

  const mesh = new THREE.Mesh(geometry, material)
  mesh.receiveShadow = true
  return mesh
}
